import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Link2Off, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import RentFundView, { RentFundStatus } from "@/components/RentFundView";
import { getRentFund, saveRentFund, createInvestorLink, revokeInvestorLink } from "@/lib/api";

const REFRESH_MS = 60_000;
const EMPTY_PLAN = { rent: "", deadline: "", start: "", ads: "", utilities: "", salaries: "", pending: [] };

function PlanForm({ initial, currency, onSaved, onCancel }) {
  const [f, setF] = useState(() => ({ ...EMPTY_PLAN, ...initial, pending: (initial?.pending || []).map((x) => ({ ...x })) }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setPending = (i, k, v) => setF((p) => ({ ...p, pending: p.pending.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.start || !f.deadline) return toast.error("Pick the tracking start date and the rent deadline");
    setSaving(true);
    try {
      const body = {
        rent: Number(f.rent) || 0, deadline: f.deadline, start: f.start,
        ads: Number(f.ads) || 0, utilities: Number(f.utilities) || 0, salaries: Number(f.salaries) || 0,
        pending: f.pending.map((x) => ({ label: x.label, amount: Number(x.amount) || 0, due: x.due || f.deadline, kind: x.kind || "other" })),
      };
      const data = await saveRentFund(body);
      toast.success("Plan saved");
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the plan");
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, type = "number") => (
    <div className="space-y-1.5">
      <Label htmlFor={`rf-${k}`} className="text-xs text-zinc-400">{label}</Label>
      {type === "date" ? (
        <DatePicker value={f[k]} onChange={set(k)} data-testid={`rf-input-${k}`} />
      ) : (
      <Input id={`rf-${k}`} type={type} min={type === "number" ? 0 : undefined} required={["rent", "deadline", "start"].includes(k)}
        value={f[k]} onChange={set(k)} className="bg-[#0a0a0a] border-[#262626] tabular-nums" data-testid={`rf-input-${k}`} />
      )}
    </div>
  );

  return (
    <form onSubmit={submit} className="card-surface p-6 space-y-5" data-testid="rent-fund-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {field("rent", `Rent due (${currency})`)}
        {field("deadline", "Rent deadline", "date")}
        {field("start", "Tracking starts", "date")}
        {field("salaries", "Salaries / month")}
        {field("ads", "Ads / month")}
        {field("utilities", "Utilities / month")}
      </div>
      <div className="space-y-2">
        <div className="eyebrow">Pending one-off payments</div>
        {f.pending.length === 0 && <p className="text-sm text-zinc-500">None. Add arrears or other one-off bills that need clearing.</p>}
        {f.pending.length > 0 && <p className="text-xs text-zinc-500">"Paid from" picks the Expenses category that clears it: Salaries entries pay the oldest salary owed first, Rent entries pay pending rent before the rent itself.</p>}
        {f.pending.map((x, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_130px_150px_160px_auto] gap-2 items-center">
            <Input aria-label="Payment name" placeholder="e.g. Pending salary" value={x.label}
              onChange={(e) => setPending(i, "label", e.target.value)} className="bg-[#0a0a0a] border-[#262626]" />
            <Input aria-label={`Amount (${currency})`} type="number" min={0} placeholder="Amount" value={x.amount}
              onChange={(e) => setPending(i, "amount", e.target.value)} className="bg-[#0a0a0a] border-[#262626] tabular-nums" />
            <select aria-label="Paid from" value={x.kind || "other"} onChange={(e) => setPending(i, "kind", e.target.value)}
              className="h-9 rounded-md border border-[#262626] bg-[#0a0a0a] px-2 text-sm text-zinc-200">
              <option value="salaries">Paid from Salaries</option>
              <option value="rent">Paid from Rent</option>
              <option value="other">Other (not tracked)</option>
            </select>
            <DatePicker value={x.due || ""} onChange={(e) => setPending(i, "due", e.target.value)} placeholder="Pay by" />
            <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${x.label || "payment"}`}
              onClick={() => setF((p) => ({ ...p, pending: p.pending.filter((_, j) => j !== i) }))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="border-[#262626]"
          onClick={() => setF((p) => ({ ...p, pending: [...p.pending, { label: "", amount: "", due: p.deadline, kind: "salaries" }] }))}>
          <Plus className="h-4 w-4 mr-1" /> Add pending payment
        </Button>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="rf-save">
          {saving ? "Saving…" : "Save plan"}
        </Button>
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  );
}

function InvestorLink({ token, onChange }) {
  const [busy, setBusy] = useState(false);
  const url = token ? `${window.location.origin}/investor/${token}` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy. Select the link and copy it manually."); }
  };
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); onChange(r.investor_token); toast.success(msg); }
    catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6 space-y-3" data-testid="investor-link">
      <div>
        <h3 className="font-display text-xl font-extrabold tracking-tight">Investor link</h3>
        <p className="text-sm text-zinc-400 mt-1">
          Anyone with this link sees the summary below, read-only and without signing in. It shows weekly totals
          and the cost plan only, never member names or individual sales.
        </p>
      </div>
      {token ? (
        <>
          <div className="flex gap-2 flex-wrap">
            <Input readOnly value={url} onFocus={(e) => e.target.select()} className="bg-[#0a0a0a] border-[#262626] font-mono text-xs flex-1 min-w-[240px]" data-testid="investor-url" />
            <Button onClick={copy} className="bg-blue-600 hover:bg-blue-500"><Copy className="h-4 w-4 mr-1.5" />Copy</Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" disabled={busy} className="border-[#262626]"
              onClick={() => run(createInvestorLink, "New link created. The old one no longer works.")}>
              <RefreshCw className="h-4 w-4 mr-1.5" />Replace link
            </Button>
            <Button variant="outline" size="sm" disabled={busy} className="border-[#262626] text-rose-300"
              onClick={() => run(revokeInvestorLink, "Investor link turned off")}>
              <Link2Off className="h-4 w-4 mr-1.5" />Turn off link
            </Button>
          </div>
        </>
      ) : (
        <Button disabled={busy} onClick={() => run(createInvestorLink, "Investor link created")} className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="create-investor-link">
          <Link2 className="h-4 w-4 mr-1.5" />Create investor link
        </Button>
      )}
    </section>
  );
}

export default function RentFund() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try { setData(await getRentFund()); setError(null); }
    catch (err) { setError(err?.response?.data?.detail || "Couldn't load the rent fund"); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const currency = data?.facility?.currency_code || "";

  return (
    <div className="space-y-6" data-testid="rent-fund-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">{data?.facility?.name || "Rent fund"} · revenue vs. break-even</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Rent fund</h2>
          <p className="text-sm text-zinc-400 mt-1">Weekly net revenue from sales against what's needed to pay rent and running costs on time. Updates every minute.</p>
        </div>
        {data?.configured && (
          <div className="flex items-center gap-3">
            <RentFundStatus status={data.status} />
            {!editing && (
              <Button variant="outline" className="rounded-full border-[#262626]" onClick={() => setEditing(true)} data-testid="rf-edit">
                <Pencil className="h-4 w-4 mr-1.5" />Edit plan
              </Button>
            )}
          </div>
        )}
      </div>

      {error && <div className="card-surface p-4 text-sm text-rose-300">{error}</div>}
      {!data && !error && <div className="card-surface p-6 text-sm text-zinc-500">Loading…</div>}

      {data && !data.configured && (
        <>
          <div className="card-surface p-5 text-sm text-zinc-300">
            No plan yet for {data.facility.name}. Enter the rent, its deadline and your monthly costs to start tracking.
          </div>
          <PlanForm initial={EMPTY_PLAN} currency={currency} onSaved={(d) => setData(d)} />
        </>
      )}

      {data?.configured && editing && (
        <PlanForm initial={data.plan} currency={currency} onCancel={() => setEditing(false)}
          onSaved={(d) => { setData(d); setEditing(false); }} />
      )}

      {data?.configured && (
        <>
          <RentFundView data={data} />
          <InvestorLink token={data.investor_token} onChange={(t) => setData((d) => ({ ...d, investor_token: t }))} />
        </>
      )}
    </div>
  );
}
